import { ProductList } from '@/components/products/ProductList'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Product as ApiProduct, Category } from '@/types/api'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const ProductsPage: React.FC = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [products, setProducts] = useState<ApiProduct[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states moved from ProductList
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  // Initial load without filters
  const loadInitialProducts = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.getProducts({
        page: currentPage,
        per_page: itemsPerPage,
      })
      setProducts(response.data)
      // Access pagination data from meta object
      setTotalItems(response.meta?.total || 0)
      setTotalPages(response.meta?.last_page || 1)
      setCurrentPage(response.meta?.current_page || 1)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load products. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast, currentPage, itemsPerPage])

  // Load products with filters
  const loadProducts = useCallback(
    async (filters?: { search?: string; status?: string; category?: string }, page: number = 1) => {
      setIsLoading(true)
      try {
        const params: Record<string, string | number> = {
          page,
          per_page: itemsPerPage,
        }

        if (filters?.search) {
          params.search = filters.search
        }

        if (filters?.status && filters.status !== 'all') {
          params.status = filters.status
        }

        if (filters?.category && filters.category !== 'all') {
          params.category_id = filters.category
        }

        const response = await apiClient.getProducts(params)
        setProducts(response.data)
        // Access pagination data from meta object
        setTotalItems(response.meta?.total || 0)
        setTotalPages(response.meta?.last_page || 1)
        setCurrentPage(response.meta?.current_page || 1)
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load products. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    },
    [toast, itemsPerPage]
  )

  const loadCategories = useCallback(async () => {
    try {
      const response = await apiClient.getCategories({ per_page: 100 })
      setCategories(response.data || [])
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load categories. Please try again.',
        variant: 'destructive',
      })
    }
  }, [toast])

  // Ref to track previous filters
  const previousFiltersRef = useRef({
    search: '',
    status: 'all',
    category: 'all',
  })

  const debounceTimerRef = useRef<number | null>(null)

  // Initial load effect - only runs once on mount
  useEffect(() => {
    loadInitialProducts()
    loadCategories()
  }, [loadInitialProducts, loadCategories])

  // Effect to handle API calls when filters change
  useEffect(() => {
    // Skip on initial render
    if (
      previousFiltersRef.current.search === '' &&
      previousFiltersRef.current.status === 'all' &&
      previousFiltersRef.current.category === 'all' &&
      searchTerm === '' &&
      statusFilter === 'all' &&
      categoryFilter === 'all'
    ) {
      return
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = window.setTimeout(() => {
      const currentFilters = {
        search: searchTerm,
        status: statusFilter,
        category: categoryFilter,
      }

      // Only call API if filters actually changed
      const hasChanged =
        previousFiltersRef.current.search !== currentFilters.search ||
        previousFiltersRef.current.status !== currentFilters.status ||
        previousFiltersRef.current.category !== currentFilters.category

      if (hasChanged) {
        // Reset to page 1 when filters change
        setCurrentPage(1)
        loadProducts(
          {
            search: currentFilters.search || undefined,
            status: currentFilters.status !== 'all' ? currentFilters.status : undefined,
            category: currentFilters.category !== 'all' ? currentFilters.category : undefined,
          },
          1
        )

        // Update previous filters
        previousFiltersRef.current = currentFilters
      }
    }, 500) // 500ms debounce

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchTerm, statusFilter, categoryFilter, loadProducts])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
  }

  const handleCategoryFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryFilter(e.target.value)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    loadProducts(
      {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      },
      page
    )
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1) // Reset to first page when changing items per page
    loadProducts(
      {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      },
      1
    )
  }

  const listProducts = useMemo(
    () =>
      products.map((product) => ({
        id: product.id,
        name: product.name,
        sku: product.sku,
        price: product.price,
        priceMin: product.price_min ?? product.price,
        priceMax: product.price_max ?? product.price,
        stock: product.stock_quantity,
        variantCount: product.variant_count ?? product.variants?.length ?? 0,
        hasVariants: Boolean(product.has_variants),
        status: product.status === 'out_of_stock' ? 'inactive' : product.status,
        featured: Boolean(product.is_featured),
        category: product.category?.name ?? '',
        image: product.images.find((img) => img.is_primary)?.image_url,
        createdAt: product.created_at,
      })),
    [products]
  )

  const handleAddProduct = () => {
    navigate('/products/add')
  }

  const handleViewProduct = (id: string) => {
    navigate(`/products/${id}`)
  }

  const handleDeleteProduct = async (id: string) => {
    if (confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        await apiClient.deleteProduct(id)

        toast({
          title: 'Success',
          description: 'Product deleted successfully',
        })

        loadProducts()
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to delete product. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  const handleToggleFeatured = async (id: string, featured: boolean) => {
    try {
      await apiClient.toggleProductFeatured(id, featured)
      await loadProducts()

      toast({
        title: 'Success',
        description: 'Product featured status updated',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update featured status. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (id: string, status: 'active' | 'inactive' | 'draft') => {
    try {
      const isActive = status === 'active'
      await apiClient.toggleProductPublish(id, isActive)
      await loadProducts()

      toast({
        title: 'Success',
        description: 'Product status updated',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update product status. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Products</h1>
        <p className="text-muted-foreground">
          Manage product catalog, specifications, and inventory
        </p>
      </div>

      <ProductList
        products={listProducts}
        isLoading={isLoading}
        categories={categories}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearchChange={handleSearchChange}
        onStatusFilterChange={handleStatusFilterChange}
        onCategoryFilterChange={handleCategoryFilterChange}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onAddProduct={handleAddProduct}
        onViewProduct={handleViewProduct}
        onDeleteProduct={handleDeleteProduct}
        onToggleFeatured={handleToggleFeatured}
        onToggleStatus={handleToggleStatus}
        onRefresh={loadProducts}
      />
    </div>
  )
}

export default ProductsPage
